import { useMemo, type ReactNode } from 'react'
import { Box, Button, Paper, Stack, Typography, ThemeProvider, useTheme } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import HubOutlinedIcon from '@mui/icons-material/HubOutlined'
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined'
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined'

import { APP_LOGO, APP_NAME } from '../lib/branding'
import { withPrimaryColor } from '../theme'

// Pulled directly from the brand mark (logo.svg) — the deep navy of the
// gear dial and the orange of the trend arrow. Swapping the whole
// screen's primary color (buttons, links, focus rings) to the logo's
// own navy — not just the decorative hero panel — via `withPrimaryColor`
// keeps every element on this page reading as one brand, not a hero
// banner bolted onto an unrelated teal form.
const LOGO_NAVY = '#113F80'
const LOGO_ORANGE = '#E48F18'

interface Props {
  title: ReactNode
  subtitle?: ReactNode
  children: ReactNode
  /** Top-bar swap link, e.g. login → register */
  swap?: { prompt: string; cta: string; to: string }
  /** Override the brand-panel headline copy on a per-page basis */
  brand?: { eyebrow: string; headline: ReactNode; tagline: string }
}

const FEATURES = [
  { icon: HubOutlinedIcon, color: LOGO_NAVY, title: 'Multi-site visibility', subtitle: 'Manage all your sites from one platform.' },
  { icon: BoltOutlinedIcon, color: LOGO_ORANGE, title: 'Real-time operations', subtitle: 'Keep work moving in real time.' },
  { icon: InsightsOutlinedIcon, color: LOGO_NAVY, title: 'Actionable insights', subtitle: 'Turn data into better decisions.' },
  { icon: VerifiedOutlinedIcon, color: LOGO_ORANGE, title: 'Built for compliance', subtitle: 'Stay audit-ready with confidence.' },
]

const DEFAULT_BRAND = {
  eyebrow: 'Integrated Facility Management',
  headline: (
    <>
      From operations
      <br />
      to{' '}
      <Box component="span" sx={{ color: LOGO_NAVY }}>
        impact.
      </Box>
    </>
  ),
  tagline:
    'A unified platform to manage assets, work orders, and compliance — across every site, every team, every day.',
}

export function AuthShell({ title, subtitle, children, swap, brand }: Props) {
  const b = brand ?? DEFAULT_BRAND
  const baseTheme = useTheme()
  const authTheme = useMemo(() => withPrimaryColor(baseTheme, LOGO_NAVY), [baseTheme])

  return (
    <ThemeProvider theme={authTheme}>
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'grid',
        // Split: brand panel on the left (lg+), form on the right
        gridTemplateColumns: { xs: '1fr', lg: '1.05fr 1fr' },
        bgcolor: 'background.default',
      }}
    >
      {/* ─────────────── BRAND PANEL ─────────────── */}
      <Box
        sx={{
          position: 'relative',
          color: '#0A1628',
          // Photo fades in from the bottom-right, under an opaque-to-
          // transparent wash of the panel's own light blue — keeps the
          // headline/feature text (top two-thirds) on solid, readable
          // color while the building shows through lower down.
          backgroundImage:
            'linear-gradient(180deg, #EAF3FC 0%, #EAF3FC 38%, rgba(234,243,252,0.88) 52%, rgba(234,243,252,0.4) 72%, rgba(234,243,252,0.05) 100%), url(/back_img.png)',
          backgroundSize: 'cover, cover',
          backgroundPosition: 'center, bottom right',
          backgroundRepeat: 'no-repeat, no-repeat',
          overflow: 'hidden',
          display: { xs: 'none', lg: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: { lg: 6, xl: 8 },
        }}
      >
        {/* Top — logo */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <Box
            component="img"
            src={APP_LOGO}
            alt={`${APP_NAME} logo`}
            sx={{ width: 44, height: 44, display: 'inline-block' }}
          />
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: -0.3, color: '#0A1628' }}>
            {APP_NAME}
          </Typography>
        </Stack>

        {/* Middle — hero */}
        <Stack spacing={3} sx={{ position: 'relative', zIndex: 1, maxWidth: 520 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 12.5,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: '#5B6B82',
            }}
          >
            {b.eyebrow}
          </Typography>
          <Typography
            variant="h1"
            sx={{
              fontWeight: 800,
              letterSpacing: -1.2,
              fontSize: { lg: 48, xl: 56 },
              lineHeight: 1.08,
            }}
          >
            {b.headline}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: '#5B6B82', fontSize: 17, lineHeight: 1.55 }}
          >
            {b.tagline}
          </Typography>

          <Box aria-hidden sx={{ width: 48, height: 4, borderRadius: 999, bgcolor: LOGO_NAVY, mt: 1 }} />

          {/* Feature grid — 2x2 */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              columnGap: 4,
              rowGap: 2.5,
              mt: 1,
            }}
          >
            {FEATURES.map((f) => (
              <Stack key={f.title} direction="row" spacing={1.5} sx={{ maxWidth: 230 }}>
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: `${f.color}1F`,
                  }}
                >
                  <f.icon sx={{ fontSize: 20, color: f.color }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#0A1628' }}>
                    {f.title}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: '#5B6B82', mt: 0.25 }}>
                    {f.subtitle}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Box>
        </Stack>

        {/* Bottom — tagline. Frosted backing since this sits over the
            photo, whose contrast against the text varies by viewport. */}
        <Typography
          sx={{
            position: 'relative',
            zIndex: 1,
            alignSelf: 'flex-start',
            fontWeight: 700,
            fontSize: 11.5,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: '#5B6B82',
            bgcolor: 'rgba(255,255,255,0.72)',
            backdropFilter: 'blur(4px)',
            borderRadius: 999,
            px: 1.5,
            py: 0.6,
          }}
        >
          People&nbsp;&nbsp;|&nbsp;&nbsp;Places&nbsp;&nbsp;|&nbsp;&nbsp;A more efficient tomorrow
        </Typography>
      </Box>

      {/* ─────────────── FORM PANEL ─────────────── */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100dvh',
          position: 'relative',
        }}
      >
        {/* Mobile-only top bar (logo) */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{
            display: { xs: 'flex', lg: 'none' },
            px: 3,
            pt: 3,
          }}
        >
          <Box
            component="img"
            src={APP_LOGO}
            alt={`${APP_NAME} logo`}
            sx={{ width: 32, height: 32, display: 'inline-block' }}
          />
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: -0.3 }}>
            {APP_NAME}
          </Typography>
        </Stack>

        {/* Top-right swap action */}
        {swap && (
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{
              position: { xs: 'static', lg: 'absolute' },
              top: 32,
              right: 48,
              alignSelf: { xs: 'flex-end', lg: 'auto' },
              px: { xs: 3, lg: 0 },
              pt: { xs: 2, lg: 0 },
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}
            >
              {swap.prompt}
            </Typography>
            <Button
              component={RouterLink}
              to={swap.to}
              variant="outlined"
              size="medium"
              sx={{ borderRadius: 999, px: 3, fontWeight: 600 }}
            >
              {swap.cta}
            </Button>
          </Stack>
        )}

        {/* The form itself, centered */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 3, sm: 6, lg: 8 },
            py: { xs: 4, lg: 8 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: 520,
              p: { xs: 3, sm: 5 },
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 4,
              bgcolor: 'background.paper',
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                letterSpacing: -0.8,
                fontSize: { xs: 32, sm: 38 },
                lineHeight: 1.1,
                mb: 1,
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body1"
                sx={{ color: 'text.secondary', mb: 4, fontSize: 16 }}
              >
                {subtitle}
              </Typography>
            )}

            {children}
          </Paper>
        </Box>
      </Box>
    </Box>
    </ThemeProvider>
  )
}
